
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { UserProfile, UserRole } from '@/contexts/AuthContext';
import { formatPhoneToE164 } from '@/utils/supabase/courierQueries';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { buildFunctionUrl } from '@/integrations/supabase/urlBuilder';

interface UserEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: UserProfile;
}

export function UserEditDialog({ isOpen, onClose, onSuccess, user }: UserEditDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState(user.name || '');
  const [phone, setPhone] = useState(user.phone || '');
  const [role, setRole] = useState<UserRole>(user.role);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setName(user.name || '');
      setPhone(user.phone || '');
      setRole(user.role);
      setError(null);
    }
  }, [isOpen, user]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Проверка наличия хотя бы телефона
      if (!phone) {
        toast.error('Необходимо указать номер телефона');
        setError('Необходимо указать номер телефона');
        setIsLoading(false);
        return;
      }

      // Форматирование телефона если он есть
      let formattedPhone: string | null = null;
      if (phone && phone.trim() !== '') {
        try {
          formattedPhone = formatPhoneToE164(phone);
          console.log('Форматированный телефон:', formattedPhone);
        } catch (error) {
          console.error('Ошибка при форматировании телефона:', error);
          toast.error('Неверный формат номера телефона');
          setError('Неверный формат номера телефона');
          setIsLoading(false);
          return;
        }
      }
      
      console.log('Данные для обновления профиля:', { 
        name, 
        phone: formattedPhone,
        role 
      });

      // Обновляем данные в profiles
      const { error: profileError, data: profileData } = await supabase
        .from('profiles')
        .update({
          name,
          phone: formattedPhone,
          role
        })
        .eq('id', user.id)
        .select();

      console.log('Результат обновления профиля:', { profileError, profileData });

      if (profileError) {
        throw profileError;
      }

      // Проверяем, изменились ли телефон или имя
      if (formattedPhone !== user.phone || name !== user.name) {
        // Вызываем edge function для обновления auth.users
        const sessionData = await supabase.auth.getSession();
        const accessToken = sessionData.data.session?.access_token;
        
        console.log('Токен доступа для запроса:', accessToken ? 'Получен' : 'Отсутствует');
        
        // Используем параметризованный URL для Edge Function
        const functionUrl = buildFunctionUrl('update-user');
        
        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          body: JSON.stringify({
            userId: user.id,
            phone: formattedPhone,
            name: name // Передаем имя
          })
        });

        const result = await response.json();
        console.log('Результат обновления auth.users:', result);

        if (!response.ok) {
          throw new Error(result.error || 'Ошибка при обновлении данных пользователя');
        }
      }

      toast.success('Профиль пользователя успешно обновлен');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Ошибка при обновлении профиля:', error);
      setError(`Ошибка при обновлении профиля: ${error.message}`);
      toast.error(`Ошибка при обновлении профиля: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Редактирование пользователя</DialogTitle>
          <DialogDescription>
            Отредактируйте информацию о пользователе.
          </DialogDescription>
        </DialogHeader>
        
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Имя</Label>
              <Input
                id="name"
                placeholder="Введите имя"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="phone">Телефон</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+7 (999) 123-45-67"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="role">Роль</Label>
              <Select value={role} onValueChange={(value) => setRole(value as UserRole)}>
                <SelectTrigger>
                  <SelectValue placeholder="Выберите роль" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Администратор</SelectItem>
                  <SelectItem value="courier">Курьер</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
